import React, { useState } from "react"
import { Link, graphql } from "gatsby"
import tw from "twin.macro"
import Image from "gatsby-image"

import {
  Layout,
  SEO,
  Button,
  Card,
  Carousel,
  LeftArrow,
  RightArrow,
  Product as CProduct,
} from "../components"
import { getDiscountedPrice, IPrice, forDisplay } from "../helpers"
import { ProductBySlugQuery, SitePageContext } from "../../graphql-types"
import styled from "@emotion/styled"
import { ArrowRight } from "react-feather"

interface Props {
  data: ProductBySlugQuery
  pageContext: SitePageContext
  location: Location
}

const ProductTemplate: React.FC<Props> = ({ data, location }) => {
  const [quantity, setQuantity] = useState(1)

  const product = data.markdownRemark
  const productDetails = product.frontmatter
  const discountedProducts = data.discountedProducts.edges
  const siteTitle = data.site.siteMetadata.title

  return (
    <Layout location={location} title={siteTitle}>
      <SEO title={productDetails.title} description={product.excerpt} />
      <Product.Wrapper>
        <Product.ImageWrapper>
          <Product.Image
            fluid={productDetails.image.childImageSharp.fluid}
            alt={`${productDetails.title}`}
            imgStyle={{
              objectFit: "contain",
            }}
          />
        </Product.ImageWrapper>
        <Product.Info>
          <Product.Title>{productDetails.title}</Product.Title>
          <Product.Description>
            {productDetails.description}
          </Product.Description>
          <Product.PriceBox>
            Price:&nbsp;
            <Product.Price>
              {productDetails.price.discount
                ? forDisplay.discountedPrice(
                    productDetails.price.original,
                    productDetails.price.discount,
                    productDetails.suffix
                  )
                : forDisplay.price(
                    productDetails.price.original,
                    productDetails.suffix
                  )}
            </Product.Price>
            {!!productDetails.price.discount && (
              <Product.DiscountedPrice>
                <OldPrice>
                  {forDisplay.price(
                    productDetails.price.original,
                    productDetails.suffix
                  )}
                </OldPrice>
                <span>·</span>
                <span>
                  {forDisplay.discount(productDetails.price.discount)}
                </span>
              </Product.DiscountedPrice>
            )}
          </Product.PriceBox>
          <AddToCart.Wrapper>
            <Quantity.Wrapper suffix={productDetails.suffix}>
              <Quantity.Input
                min="1"
                max="99"
                type="number"
                value={quantity}
                onChange={({ target: { value } }: any) => {
                  const newValue = parseInt(value, 10)
                  if ((newValue > 0 && newValue <= 99) || isNaN(newValue)) {
                    setQuantity(value)
                  }
                }}
              />
            </Quantity.Wrapper>
            <AddToCart.Button
              className="snipcart-add-item"
              aria-label="add to cart"
              data-item-id={productDetails.id}
              data-item-price={
                productDetails.price.discount
                  ? getDiscountedPrice(
                      productDetails.price.original,
                      productDetails.price.discount
                    )
                  : productDetails.price.original
              }
              data-item-image={
                productDetails.image.childImageSharp.fluid.originalImg
              }
              data-item-name={productDetails.title}
              data-item-description={productDetails.description}
              data-item-url={location.pathname}
              data-item-quantity={quantity}
            >
              Add to cart
            </AddToCart.Button>
          </AddToCart.Wrapper>
          <ProductDetails.Wrapper>
            <ProductDetails.Header>Description</ProductDetails.Header>
            <ProductDetails.Details>
              {product.rawMarkdownBody}
            </ProductDetails.Details>
          </ProductDetails.Wrapper>
        </Product.Info>
      </Product.Wrapper>

      <AdditionalProducts.Wrapper>
        <AdditionalProducts.Card>
          <AdditionalProducts.TextBox>
            <AdditionalProducts.Title>You may like...</AdditionalProducts.Title>
            <AdditionalProducts.Link to="/deals/">
              See more <ArrowRight />
            </AdditionalProducts.Link>
          </AdditionalProducts.TextBox>
          <AdditionalProducts.Carousel
            leftArrow={LeftArrow}
            rightArrow={RightArrow}
            autoplay={{ delay: 5000 }}
            slidesPerView={1}
            centerSlides
            breakpoints={{
              798: {
                slidesPerView: 5,
              },
              680: {
                slidesPerView: 4,
              },
              426: {
                slidesPerView: 3,
                centerSlides: false,
              },
            }}
          >
            {discountedProducts
              .filter(
                (discountedProduct) =>
                  productDetails.id !== discountedProduct.node.frontmatter.id
              )
              .map(
                ({
                  node: {
                    fields: { slug },
                    frontmatter: { id, title, image, price, suffix },
                  },
                }) => (
                  <AdditionalProducts.CarouselItem
                    key={id}
                    to={`/products/${slug}`}
                  >
                    <CProduct
                      title={title}
                      fluid={image.childImageSharp.fluid}
                      price={price as IPrice}
                      suffix={suffix}
                    />
                  </AdditionalProducts.CarouselItem>
                )
              )}
          </AdditionalProducts.Carousel>
        </AdditionalProducts.Card>
      </AdditionalProducts.Wrapper>
    </Layout>
  )
}

const Product = {
  Wrapper: tw.div`flex flex-col items-center justify-around flex-1 mx-auto mt-16 mb-6 md:my-16 xl:max-w-screen-xl md:flex-row`,
  ImageWrapper: tw.div`flex justify-center flex-1`,
  Image: tw(Image)`w-64 h-64`,
  Info: tw.div`flex flex-col items-center flex-1 px-4 pt-4 md:items-start md:px-0 md:pt-0`,
  Title: tw.h1`text-5xl font-bold text-center md:text-6xl md:text-left`,
  Description: tw.h2`text-lg font-semibold text-center md:text-left`,
  PriceBox: tw.div`flex items-center mt-4 text-sm font-light text-gray-600`,
  Price: tw.h3`flex flex-col text-lg font-semibold text-red-600`,
  DiscountedPrice: styled.span`
    ${tw`flex items-center ml-2 text-sm font-semibold text-gray-500`}

    & > *:not(:last-child) {
      ${tw`mr-2`}
    }
  `,
}

const OldPrice = tw.span`line-through`

const AddToCart = {
  Wrapper: tw.div`flex mt-4`,
  Button: tw(
    Button
  )`self-start bg-fresh-green text-white hover:bg-fresh-green border-fresh-green ml-2 hover:text-white`,
}

const Quantity = {
  Wrapper: styled.div<{ suffix: string }>`
    ${tw`relative inline-block`}

    &::after {
      content: "${({ suffix }) => suffix}";
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 2em;
      transition: all 0.05s ease-in-out;
    }
  `,
  Input: styled.input`
    ${tw`w-24 p-2 border-2 border-black focus:outline-none bg-background-gray`}

    &::-webkit-inner-spin-button, 
    &::-webkit-outer-spin-button {
      opacity: 1;
    }
  `,
}

const ProductDetails = {
  Wrapper: tw.div`flex flex-col items-center mt-8 md:items-start`,
  Header: tw.h4`text-lg font-semibold`,
  Details: tw.p`mt-4 text-base text-center text-gray-600 lg:pr-2 xl:pr-0 md:text-left`,
}

const AdditionalProducts = {
  Wrapper: tw.div`w-full px-2 mx-auto mt-6 xl:px-0 lg:max-w-screen-lg xl:max-w-screen-xl`,
  Card: tw(Card)`p-6 flex flex-col justify-center justify-around`,
  TextBox: tw.div`flex flex-wrap items-center mb-2`,
  Title: tw.h1`text-2xl text-black`,
  Link: styled(Link)`
    ${tw`flex items-center ml-2 text-sm`}

    & > svg {
      ${tw`transition-transform duration-200 ease-in-out`}
    }

    &:hover > svg {
      ${tw`transform translate-x-1`}
    }
  `,
  Carousel: tw(Carousel)`flex items-center justify-around`,
  CarouselItem: tw(Link)`flex justify-center`,
}

export default ProductTemplate

export const pageQuery = graphql`
  query ProductBySlug($slug: String!) {
    site {
      siteMetadata {
        title
      }
    }
    markdownRemark(fields: { slug: { eq: $slug } }) {
      id
      excerpt(pruneLength: 160)
      rawMarkdownBody
      html
      frontmatter {
        title
        date(formatString: "MMMM DD, YYYY")
        id
        suffix
        price {
          original
          discount
        }
        image {
          childImageSharp {
            fluid {
              ...GatsbyImageSharpFluid
              originalImg
            }
          }
        }
        description
      }
    }
    discountedProducts: allMarkdownRemark(
      limit: 11
      filter: { frontmatter: { price: { discount: { gt: 0 } } } }
      sort: { order: DESC, fields: frontmatter___date }
    ) {
      edges {
        node {
          excerpt
          fields {
            slug
          }
          frontmatter {
            id
            title
            suffix
            price {
              original
              discount
            }
            image {
              childImageSharp {
                fluid {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
      }
    }
  }
`
